import React, { useEffect, useState } from 'react'
import { useGetAuthenticatedUser } from 'Flux/Selector/index.js'
import { isGlobalAdmin } from 'Model/User/IUser.js'
import {
  MAX_RESIDENT_TABLES,
  getResidentTableSummaries,
  type ResidentTableSummary
} from '../../../Repository/Visualization/CubeTableRegistry.js'
import {
  MAX_ENTRIES,
  getCachedCubeSummaries,
  type CachedCubeSummary
} from '../../../Repository/Visualization/CubeCache.js'
import {
  getCubeDiagCounters,
  getCubeDiagEvents,
  subscribeCubeDiag,
  type CubeDiagCounters,
  type CubeDiagEvent
} from '../../../Repository/Visualization/CubeDiagnostics.js'

const REFRESH_MS = 1500

const fmtBytes = (b: number): string => {
  if (b <= 0) return '0'
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

const sum = (xs: number[]): number => xs.reduce((a, b) => a + b, 0)

const KIND_LABEL: Record<CubeDiagEvent['kind'], string> = {
  loaded: 'loaded',
  'idb-evict': 'IDB evict',
  'idb-quota': 'IDB quota!',
  'idb-error': 'IDB error',
  'registry-evict': 'RAM evict'
}

/**
 * Admin-only diagnostics for the visualization cube caches. Surfaces the two bounded caches —
 * the in-memory decoded-table registry (`MAX_RESIDENT_TABLES`) and the IndexedDB byte cache
 * (`MAX_ENTRIES`) — plus eviction / quota counters, so that when testing against a large dataset
 * you can *see* when a cap or the IndexedDB quota is hit rather than silently losing the cache.
 *
 * Gated on `showAdminUx && isGlobalAdmin` (same gate as the TopBar admin affordances). Renders
 * nothing for everyone else, so it's safe to mount unconditionally in the workspace header.
 *
 */
export default function CubeDiagnosticsPanel(): React.ReactElement | null {
  const user = useGetAuthenticatedUser()
  const isAdmin = !!user?.showAdminUx && isGlobalAdmin(user)

  const [open, setOpen] = useState(false)
  const [resident, setResident] = useState<ResidentTableSummary[]>([])
  const [cached, setCached] = useState<CachedCubeSummary[]>([])
  const [counters, setCounters] = useState<CubeDiagCounters>(getCubeDiagCounters)
  const [events, setEvents] = useState<CubeDiagEvent[]>([])

  useEffect(() => {
    if (!isAdmin) return

    let alive = true
    const refresh = () => {
      if (!alive) return
      setResident(getResidentTableSummaries())
      setCounters(getCubeDiagCounters())
      setEvents(getCubeDiagEvents().slice(-8).reverse())
      void getCachedCubeSummaries().then((s) => {
        if (alive) setCached(s)
      })
    }

    refresh()
    const unsub = subscribeCubeDiag(refresh)
    const id = window.setInterval(refresh, REFRESH_MS)
    return () => {
      alive = false
      unsub()
      window.clearInterval(id)
    }
  }, [isAdmin])

  if (!isAdmin) return null

  const residentBytes = sum(resident.map((r) => r.bytes))
  const cachedBytes = sum(cached.map((c) => c.bytes))
  const residentAtCap = resident.length >= MAX_RESIDENT_TABLES
  const cachedAtCap = cached.length >= MAX_ENTRIES
  const problems = counters.idbEvictions + counters.idbQuotaErrors + counters.idbErrors + counters.registryEvictions

  return (
    <div className="relative print:hidden">
      <button
        className={`btn btn-sm ${problems > 0 ? 'btn-warning' : 'btn-ghost'}`}
        onClick={() => setOpen((o) => !o)}
        title="Cube cache diagnostics (admin)"
        data-testid="cube-diag-toggle"
      >
        ⛁ Cube
        {problems > 0 && <span className="badge badge-sm badge-error ml-1">{problems}</span>}
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-96 rounded-lg border border-gray-300 bg-base-100 p-3 text-xs shadow-xl"
          data-testid="cube-diag-panel"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="font-semibold">Cube cache diagnostics</span>
            <span className="opacity-60">admin · refreshes {REFRESH_MS / 1000}s</span>
          </div>

          {/* Resident decoded tables (RAM) */}
          <div className="mb-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">
                Decoded tables (RAM){' '}
                <span className={residentAtCap ? 'text-warning font-semibold' : ''}>
                  {resident.length}/{MAX_RESIDENT_TABLES}
                  {residentAtCap && ' ⚠ at cap'}
                </span>
              </span>
              <span className="opacity-70">{fmtBytes(residentBytes)}</span>
            </div>
            <ul className="mt-1 space-y-0.5">
              {resident.length === 0 && <li className="opacity-50">none resident</li>}
              {resident.map((r) => (
                <li key={r.key} className="flex justify-between font-mono">
                  <span className="truncate" title={r.key}>
                    {r.key}
                  </span>
                  <span className="ml-2 whitespace-nowrap opacity-70">
                    {r.rows.toLocaleString()} rows · {fmtBytes(r.bytes)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* IndexedDB persisted cubes */}
          <div className="mb-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">
                IndexedDB cubes{' '}
                <span className={cachedAtCap ? 'text-warning font-semibold' : ''}>
                  {cached.length}/{MAX_ENTRIES}
                  {cachedAtCap && ' ⚠ at cap'}
                </span>
              </span>
              <span className="opacity-70">{fmtBytes(cachedBytes)}</span>
            </div>
            <ul className="mt-1 space-y-0.5">
              {cached.length === 0 && <li className="opacity-50">none cached</li>}
              {cached.map((c) => (
                <li key={c.key} className="flex justify-between font-mono">
                  <span className="truncate" title={c.key}>
                    {c.key}
                  </span>
                  <span className="ml-2 whitespace-nowrap opacity-70">{fmtBytes(c.bytes)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Counters */}
          <div className="mb-2 grid grid-cols-2 gap-x-3 gap-y-0.5">
            <span>loaded: {counters.loaded}</span>
            <span className={counters.registryEvictions > 0 ? 'text-warning' : ''}>
              RAM evictions: {counters.registryEvictions}
            </span>
            <span className={counters.idbEvictions > 0 ? 'text-warning' : ''}>
              IDB evictions: {counters.idbEvictions}
            </span>
            <span className={counters.idbQuotaErrors > 0 ? 'text-error font-semibold' : ''}>
              IDB quota errors: {counters.idbQuotaErrors}
            </span>
            {counters.idbErrors > 0 && (
              <span className="text-error">IDB errors: {counters.idbErrors}</span>
            )}
          </div>

          {(counters.idbQuotaErrors > 0 || counters.idbEvictions > 0 || counters.registryEvictions > 0) && (
            <div className="mb-2 rounded bg-warning/15 p-1.5 text-warning-content">
              Cache pressure detected — cubes are being evicted/refused. Switching scope or blast
              type may now trigger a fresh fetch + decode instead of a cache hit.
            </div>
          )}

          {/* Recent events */}
          <div>
            <div className="font-medium">Recent events</div>
            <ul className="mt-1 space-y-0.5">
              {events.length === 0 && <li className="opacity-50">none yet</li>}
              {events.map((e, i) => (
                <li key={`${e.at}-${i}`} className="flex justify-between font-mono">
                  <span className={e.kind === 'idb-quota' || e.kind === 'idb-error' ? 'text-error' : ''}>
                    {KIND_LABEL[e.kind]}
                  </span>
                  <span className="ml-2 truncate opacity-70" title={e.key ?? ''}>
                    {e.key ?? e.detail ?? ''}
                    {e.rows !== undefined ? ` · ${e.rows.toLocaleString()} rows` : ''}
                    {e.bytes !== undefined ? ` · ${fmtBytes(e.bytes)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
